import { ReactNode } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'next-i18next'

import {
  DEFAULT_THEME_COLORS,
  DEFAULT_BORDER_STYLES,
  DEFAULT_BOX_SHADOW,
  DEFAULT_FOCUS_STYLES,
  getBodyFonts,
} from 'css/theme'
import { UiIcon } from 'modules/ui/atoms/Icon'
import { breakpointMaxQuery } from 'utils/functions/breakpoints'

export interface AlertBannerProps {
  children: ReactNode
  onClose: () => void
}

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: fixed;
  z-index: 100;
  top: 0;
  left: 16px;
  right: 16px;
  max-width: 990px;
  margin: 24px auto;
  padding-left: 12px;
  background-color: ${DEFAULT_THEME_COLORS['--theme-color--status-error-lightest']};
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-10']};
  border-radius: ${DEFAULT_BORDER_STYLES['--theme--border-radius-md']};
  border: ${DEFAULT_BORDER_STYLES['--theme--border-input-error']};
  box-shadow: ${DEFAULT_BOX_SHADOW};

  ${breakpointMaxQuery('xs')} {
    margin: 0;
    left: 0;
    right: 0;
    border-radius: unset;
    border: unset;
    border-bottom: ${DEFAULT_BORDER_STYLES['--theme--border-input-error']};
    align-items: flex-start;
  }
`

const LeftItems = styled.div({
  display: 'inline-flex',
  padding: '12px 0',
  ...getBodyFonts(),
})

const RightItem = styled.div`
  cursor: pointer;
  padding: 12px;
  height: 44px;
  margin: 4px;
  align-self: self-start;

  &:focus {
    outline: ${DEFAULT_FOCUS_STYLES['--theme--focus-outline']};
    outline-offset: 1px;
  }

  ${breakpointMaxQuery('xs')} {
    padding: 12px 10px;
  }
`

const AlertBanner = ({ children, onClose }: AlertBannerProps) => {
  const { t } = useTranslation()

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClose()
    }
  }

  return (
    <Banner role="alert" aria-live="polite">
      <LeftItems>
        <span style={{ height: '32px' }}>
          <UiIcon
            name="error"
            color={DEFAULT_THEME_COLORS['--theme-color--status-error']}
            w={32}
            h={32}
          />
        </span>
        <span style={{ padding: '4px 0 0 4px' }}>{children}</span>
      </LeftItems>
      <RightItem
        onClick={onClose}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label={t('AlertBanner.closeAlertLabel', 'close alert')}
      >
        <UiIcon name="close" />
      </RightItem>
    </Banner>
  )
}

export default AlertBanner
